import { t } from '@lingui/core/macro';
import dayjs from 'dayjs';
import { type Point, type PointerEventObject } from 'highcharts';
import { useMemo } from 'react';

import type { OfflineEvent, OfflineTimelineDataType } from '@/api/offlineEvents';
import Flare, {
  Axis,
  Bubble,
  type ChartCallbackFunction,
  type FlareSeriesOptions,
  Tooltip,
} from '@/components/Flare';
import { MonthDateFormat, MonthDateShortFormat, MonthDateYearFormat } from '@/constants';
import { cn } from '@/lib/utils';
import {
  COLOR_AQUA_300,
  COLOR_ORANGE_400,
  COLOR_PINK_400,
  COLOR_TEAL_400,
  COLOR_VIOLET_400,
  COLOR_YELLOW_300,
} from '@/styles/palette';

import { NonDigitalEventsTimelineChartTooltip, NonDigitalEventsTimelineLoader } from './';

const LOADING_HEIGHT = 240;
const MIN_CHART_HEIGHT = 150;
const CHART_PADDING = 32;
const ROW_HEIGHT = 36;

const LARGE_BUBBLE_RADIUS = 8;
const SMALL_BUBBLE_RADIUS = 5;

const COLORS = [
  COLOR_VIOLET_400,
  COLOR_ORANGE_400,
  COLOR_TEAL_400,
  COLOR_YELLOW_300,
  COLOR_PINK_400,
  COLOR_AQUA_300,
];

type Props = {
  data?: OfflineTimelineDataType[];
  isLoading?: boolean;
  error?: Error | null;
  marginLeft: number;
  marginRight: number;
  onHoverChart?: (event: PointerEventObject, point: Point) => void;
  onHoverLeaveChart?: (event: MouseEvent) => void;
  onPostRenderChart?: ChartCallbackFunction;
};

type CustomPointData = {
  events: OfflineEvent[];
  seriesName: string;
  seriesId: string;
};

const NonDigitalEventsTimelineChart = ({
  data,
  isLoading,
  error,
  marginLeft,
  marginRight,
  onHoverChart,
  onHoverLeaveChart,
  onPostRenderChart,
}: Props) => {
  const chartData: FlareSeriesOptions[] | undefined = useMemo(() => {
    if (data == null) {
      return undefined;
    }

    return data.map((series, seriesIndex) => {
      return {
        id: series.campaignType.id,
        name: `${series.campaignType.vendor.name}: ${series.campaignType.name}`,
        data: series.data
          .filter((d) => d.events.length)
          .map((d) => ({
            className: cn(
              d.events.length > 1 ? 'stroke-[10px]' : 'stroke-[13px]',
              '[stroke-opacity:0.5]',
            ),
            date: d.sundayOfWeek,
            category: seriesIndex,
            radius: d.events.length > 1 ? LARGE_BUBBLE_RADIUS : SMALL_BUBBLE_RADIUS,
            custom: {
              events: d.events,
              seriesName: series.campaignType.vendor.name,
              seriesId: series.campaignType.vendor.id,
            } as CustomPointData,
          })),
      };
    });
  }, [data]);

  const xAxisRange = useMemo(() => {
    const firstSeries = data?.[0];
    if (firstSeries == null || firstSeries.data.length === 0) {
      return undefined;
    }
    return {
      startDate: dayjs(firstSeries.data[0].sundayOfWeek),
      endDate: dayjs(firstSeries.data[firstSeries.data.length - 1].sundayOfWeek),
    };
  }, [data]);

  const calculatedHeight = data
    ? Math.max(CHART_PADDING * 2 + data.length * ROW_HEIGHT, MIN_CHART_HEIGHT)
    : LOADING_HEIGHT;

  return (
    <div style={{ height: calculatedHeight }}>
      <Flare
        className={cn('[&_.highcharts-yaxis-grid_path]:[stroke-linecap:round]')}
        data={chartData}
        description={t`A timeline chart which shows the dates of all non-digital events across channels`}
        isLoading={isLoading}
        error={error}
        chartMarginLeft={marginLeft}
        chartMarginRight={marginRight}
        postRender={onPostRenderChart}
        colors={COLORS}
        onHoverChart={onHoverChart}
        onHoverLeaveChart={onHoverLeaveChart}
      >
        <Bubble
          x="date"
          y="category"
          z="radius"
          sizeBy="width"
          minSize={10}
          maxSize={18}
          loader={<NonDigitalEventsTimelineLoader />}
          showInteractionHalo={false}
        />
        <Axis
          type="category"
          categories={chartData?.map((d) => d.name)}
          tickmarkPlacement="on"
          tickPositions={chartData?.map((_, i) => i)}
          gridlineWidth={8}
          position="right"
          reversed
          useHTML
          labelsNoWrap
        />
        <Axis
          type="datetime"
          position="bottom"
          crosshair="line"
          tickPixelInterval={100}
          labelFormat={(item) => dayjs(item.value).utc().format(MonthDateShortFormat)}
          baselineWidth={0}
          gridlineWidth={0}
          tickLength={0}
          labelDistance={0}
          min={xAxisRange?.startDate && +xAxisRange.startDate}
          max={xAxisRange?.endDate && +xAxisRange.endDate}
        />
        <Tooltip
          titleFormat={(point) => {
            const startDate = dayjs(point?.x).utc();
            const endDate = startDate.add(6, 'd');
            return `${startDate.format(MonthDateFormat)} - ${endDate.format(MonthDateYearFormat)}: ${point.custom?.seriesName}`;
          }}
          rowContent={(points) => (
            <NonDigitalEventsTimelineChartTooltip
              events={(points[0].custom as CustomPointData).events}
              name={(points[0].custom as CustomPointData).seriesName}
            />
          )}
        />
      </Flare>
    </div>
  );
};

export default NonDigitalEventsTimelineChart;
